import { NavLink, useLocation } from "react-router-dom";

interface TabItem {
  to: string;
  label: string;
  permission?: string;
  icon: React.ReactNode;
}

const tabs: TabItem[] = [
  {
    to: "/admin/dashboard",
    label: "داشبورد",
    permission: "stats.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="9" rx="1" />
        <rect x="14" y="3" width="7" height="5" rx="1" />
        <rect x="14" y="12" width="7" height="9" rx="1" />
        <rect x="3" y="16" width="7" height="5" rx="1" />
      </svg>
    ),
  },
  {
    to: "/admin/system",
    label: "سیستم",
    permission: "system.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="2" width="20" height="8" rx="2" />
        <rect x="2" y="14" width="20" height="8" rx="2" />
        <path d="M6 6h.01M6 18h.01" />
      </svg>
    ),
  },
  {
    to: "/admin/tickets",
    label: "تیکت‌ها",
    permission: "tickets.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        <line x1="9" y1="10" x2="15" y2="10" />
        <line x1="12" y1="7" x2="12" y2="13" />
      </svg>
    ),
  },
  {
    to: "/admin/users",
    label: "کاربران",
    permission: "users.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87" />
        <path d="M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
  },
  {
    to: "/admin/partners",
    label: "شرکا",
    permission: "partners.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m11 17 2 2a1 1 0 1 0 3-3" />
        <path d="m14 14 2.5 2.5a1 1 0 1 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 1 1-3-3l2.81-2.81a5.79 5.79 0 0 1 7.06-.87l.47.28a2 2 0 0 0 1.42.25L21 4" />
        <path d="m21 3 1 11h-2" />
        <path d="M3 3 2 14l6.5 6.5a1 1 0 1 0 3-3" />
        <path d="M3 4h8" />
      </svg>
    ),
  },
  {
    to: "/admin/plans",
    label: "اشتراک‌ها",
    permission: "subscriptions.view",
    icon: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="5" width="20" height="14" rx="2" />
        <line x1="2" y1="10" x2="22" y2="10" />
      </svg>
    ),
  },
];

interface AdminMobileFooterNavProps {
  permissions?: string[];
  isAdmin?: boolean;
}

export default function AdminMobileFooterNav({ permissions = [], isAdmin = false }: AdminMobileFooterNavProps) {
  const { pathname } = useLocation();

  const has = (permission?: string) =>
    isAdmin || (permission ? permissions.includes(permission) : true);

  const visible = tabs.filter((item) => has(item.permission));
  if (visible.length === 0) return null;

  const rawIndex = visible.findIndex((item) => pathname.startsWith(item.to));
  const activeIndex = rawIndex === -1 ? 0 : rawIndex;
  const activeTab = visible[activeIndex];
  const centerPercent = ((activeIndex + 0.5) / visible.length) * 100;

  return (
    <nav dir="ltr" className="lg:hidden fixed bottom-0 inset-x-0 z-40 pointer-events-none">
      <div className="mx-auto mb-3 px-6 max-w-[480px]">
        <div className="relative pointer-events-auto h-[80px]">
          {/* Circular hole (cutout) in the navbar for the active tab */}
          <div
            className="absolute"
            style={{
              top: -8,
              left: `${centerPercent}%`,
              width: 64,
              height: 64,
              borderRadius: "50%",
              background: "var(--admin-bg)",
              transform: "translateX(-50%)",
              transition: "left 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 3,
            }}
          />

          {/* Floating active tab button (white fill, green border) */}
          <div
            className="absolute flex items-center justify-center"
            style={{
              top: -1,
              left: `${centerPercent}%`,
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: "var(--admin-bg-surface)",
              border: "2px solid var(--admin-primary)",
              color: "var(--admin-primary)",
              transform: "translateX(-50%)",
              boxShadow: "0 8px 20px rgba(7, 135, 131, 0.35)",
              transition: "left 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)",
              zIndex: 4,
            }}
          >
            <span key={activeTab.to} className="admin-footer-pop">
              {activeTab.icon}
            </span>
          </div>

          {/* Pill bar */}
          <div
            className="absolute bottom-0 inset-x-0 flex justify-around px-2"
            style={{
              height: 56,
              borderRadius: 28,
              background: "var(--admin-primary-dark)",
              boxShadow: "0 12px 32px rgba(5, 96, 93, 0.35)",
              zIndex: 2,
            }}
          >
            {visible.map((tab, i) => {
              const active = i === activeIndex;
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  aria-label={tab.label}
                  className="relative flex-1 h-full transition-transform active:scale-95"
                >
                  <span
                    className="absolute inset-x-0 flex justify-center transition-opacity duration-200"
                    style={{ top: 12, opacity: active ? 0 : 1, color: "rgba(255,255,255,0.85)" }}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className="absolute inset-x-0 text-center text-[10px] leading-none whitespace-nowrap transition-all duration-300"
                    style={{
                      top: active ? 17 : 40,
                      opacity: active ? 1 : 0,
                      color: active ? "var(--admin-primary)" : "rgba(255,255,255,0.75)",
                    }}
                  >
                    {tab.label}
                  </span>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </nav>
  );
}
